import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", phone: "", subject: "", message: "" };

const inputCls =
  "w-full rounded-xl border border-white/15 bg-[#070709] px-4 py-3.5 text-sm text-white outline-none transition-colors placeholder:text-slate-600 focus:border-amber-400";
const labelCls =
  "mb-2 block font-mono-alt text-[11px] uppercase tracking-[0.25em] text-slate-500";

const Field = ({ label, testid, ...props }) => (
  <label className="block">
    <span className={labelCls}>{label}</span>
    <input data-testid={testid} required className={inputCls} {...props} />
  </label>
);

export const ContactForm = () => {
  const { t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(c.success);
      setForm(EMPTY);
    } catch {
      toast.error(c.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contatti" className="mx-auto max-w-7xl px-5 py-28 sm:px-8">
      <div className="grid gap-14 lg:grid-cols-2">
        <div>
          <SectionHeading eyebrow={c.eyebrow} title={c.title} accent={c.accent} />
          <Reveal delay={0.15}>
            <p className="max-w-md text-base leading-relaxed text-slate-400">{c.intro}</p>
            <div className="mt-10 space-y-5">
              <a
                href="tel:+41766344242"
                data-testid="contact-phone-link"
                className="block font-display text-3xl font-extrabold text-white transition-colors hover:text-amber-400 sm:text-4xl"
              >
                +41 76 634 4242
              </a>
              <a
                href="mailto:info@taxibellinzona.ch"
                data-testid="contact-email-link"
                className="block text-base text-amber-300 underline-offset-4 hover:underline"
              >
                info@taxibellinzona.ch
              </a>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-3xl border border-white/10 bg-[#101017] p-8 sm:p-10"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label={c.name} testid="contact-form-name" value={form.name} onChange={set("name")} placeholder="Mario Rossi" />
              <Field label={c.phone} testid="contact-form-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="+41 79 ..." />
            </div>
            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <Field label={c.email} testid="contact-form-email" type="email" value={form.email} onChange={set("email")} placeholder="mario@esempio.ch" />
              <Field label={c.subject} testid="contact-form-subject" value={form.subject} onChange={set("subject")} placeholder={c.subjectPlaceholder} />
            </div>
            <label className="mt-5 block">
              <span className={labelCls}>{c.message}</span>
              <textarea
                data-testid="contact-form-message"
                required
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder={c.messagePlaceholder}
                className={`${inputCls} resize-none`}
              />
            </label>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-form-submit"
              className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-amber-400 px-8 py-4 font-display text-sm font-extrabold uppercase tracking-wide text-black transition-all duration-300 hover:bg-amber-300 disabled:opacity-60 sm:w-auto"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {sending ? c.sending : c.submit}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
